import { useState } from 'react';

import { DateRangePicker, presetDateRange, type DateRangeValue } from './DateRangePicker.js';

type SampleTab = 'overview' | 'activity' | 'settings';
type SampleAlertMethod = 'browser' | 'system';

function ChevronIcon() {
	return (
		<svg aria-hidden="true" viewBox="0 0 20 20">
			<path d="m6.5 8 3.5 3.5L13.5 8" />
		</svg>
	);
}

function MoreIcon() {
	return (
		<svg aria-hidden="true" viewBox="0 0 20 20">
			<circle cx="4" cy="10" r="1.5" />
			<circle cx="10" cy="10" r="1.5" />
			<circle cx="16" cy="10" r="1.5" />
		</svg>
	);
}

function SearchIcon() {
	return (
		<svg aria-hidden="true" viewBox="0 0 20 20">
			<circle cx="8.75" cy="8.75" r="5.25" />
			<path d="m12.5 12.5 4 4" />
		</svg>
	);
}

function CloseIcon() {
	return (
		<svg aria-hidden="true" viewBox="0 0 20 20">
			<path d="m5 5 10 10M15 5 5 15" />
		</svg>
	);
}

export function UiStandards() {
	const [sampleTab, setSampleTab] = useState<SampleTab>('overview');
	const [menuOpen, setMenuOpen] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [toastVisible, setToastVisible] = useState(false);
	const [toggleEnabled, setToggleEnabled] = useState(true);
	const [messageNotificationsEnabled, setMessageNotificationsEnabled] = useState(true);
	const [sampleAlertMethod, setSampleAlertMethod] = useState<SampleAlertMethod>('browser');
	const [sampleDateRange, setSampleDateRange] = useState<DateRangeValue>(() => presetDateRange());

	return (
		<section className="ui-page ui-standards-page">
			<header className="ui-page-header">
				<div>
					<p className="ui-eyebrow">Approved reference</p>
					<h2>UI Standards</h2>
					<p className="ui-page-intro">
						Review the shared components, spacing and typography applied across the
						administration interface.
					</p>
				</div>
				<span className="ui-badge ui-badge-info">Current standard</span>
			</header>

			<div className="ui-callout ui-callout-info" role="note">
				<div className="ui-callout-mark">i</div>
				<div>
					<strong>These approved standards apply throughout the app.</strong>
					<p>
						Use the normal owner and customer workspaces to verify the same controls,
						panels, tables and feedback states in real workflows.
					</p>
				</div>
			</div>

			<nav aria-label="UI standards sections" className="ui-anchor-nav">
				<a href="#ui-foundations">Foundations</a>
				<a href="#ui-actions">Actions</a>
				<a href="#ui-forms">Forms</a>
				<a href="#ui-panels">Panels</a>
				<a href="#ui-data">Tables &amp; lists</a>
				<a href="#ui-feedback">Feedback</a>
				<a href="#ui-overlays">Overlays</a>
			</nav>

			<section className="ui-showcase-section" id="ui-foundations">
				<div className="ui-section-heading">
					<div>
						<p className="ui-eyebrow">01 · Foundations</p>
						<h3>Type, colour and rhythm</h3>
						<p>Every component below uses these shared values.</p>
					</div>
					<span className="ui-section-note">Responsive from 320px</span>
				</div>

				<div className="ui-gallery-grid ui-gallery-grid-2">
					<article className="ui-panel">
						<div className="ui-panel-heading">
							<div>
								<p className="ui-eyebrow">Typography</p>
								<h4>Clear hierarchy</h4>
							</div>
						</div>
						<div className="ui-type-scale">
							<div>
								<span>Page title</span>
								<strong className="ui-type-page">Customer overview</strong>
								<small>26–32px</small>
							</div>
							<div>
								<span>Section title</span>
								<strong className="ui-type-section">Recent conversations</strong>
								<small>20px</small>
							</div>
							<div>
								<span>Panel title</span>
								<strong className="ui-type-panel">Team availability</strong>
								<small>18px semibold</small>
							</div>
							<div>
								<span>Body</span>
								<p>Primary content remains comfortably readable.</p>
								<small>16px</small>
							</div>
							<div>
								<span>Supporting</span>
								<p className="ui-supporting">Labels, metadata and table content.</p>
								<small>14px</small>
							</div>
						</div>
					</article>

					<article className="ui-panel">
						<div className="ui-panel-heading">
							<div>
								<p className="ui-eyebrow">Palette</p>
								<h4>Restrained and purposeful</h4>
							</div>
						</div>
						<div className="ui-swatch-grid">
							<div>
								<span className="ui-swatch ui-swatch-navy" />
								<strong>Navy</strong>
								<small>Headings</small>
							</div>
							<div>
								<span className="ui-swatch ui-swatch-blue" />
								<strong>Blue</strong>
								<small>Actions</small>
							</div>
							<div>
								<span className="ui-swatch ui-swatch-pale" />
								<strong>Pale blue</strong>
								<small>Subtle fills</small>
							</div>
							<div>
								<span className="ui-swatch ui-swatch-green" />
								<strong>Green</strong>
								<small>Success</small>
							</div>
							<div>
								<span className="ui-swatch ui-swatch-amber" />
								<strong>Amber</strong>
								<small>Warning</small>
							</div>
							<div>
								<span className="ui-swatch ui-swatch-red" />
								<strong>Red</strong>
								<small>Danger</small>
							</div>
						</div>
						<div className="ui-foundation-facts">
							<div>
								<strong>4 · 8 · 12 · 16 · 24 · 32 · 48</strong>
								<span>Spacing scale</span>
							</div>
							<div>
								<strong>10px</strong>
								<span>Panel radius</span>
							</div>
							<div>
								<strong>42px</strong>
								<span>Control height</span>
							</div>
							<div>
								<strong>1600px</strong>
								<span>Content maximum</span>
							</div>
						</div>
					</article>
				</div>
			</section>

			<section className="ui-showcase-section" id="ui-actions">
				<div className="ui-section-heading">
					<div>
						<p className="ui-eyebrow">02 · Actions</p>
						<h3>Buttons and navigation</h3>
						<p>
							Standard actions are 42px high; sidebar items use 14px text, a 34px
							minimum height and no vertical branch line. On narrow screens,
							navigation remains one column and uses an off-canvas drawer with a
							persistent edge tab.
						</p>
					</div>
				</div>
				<article className="ui-panel">
					<div className="ui-button-groups">
						<div>
							<span className="ui-example-label">Standard actions</span>
							<div className="ui-action-row">
								<button className="ui-button ui-button-primary" type="button">
									Save changes
								</button>
								<button className="ui-button ui-button-secondary" type="button">
									Cancel
								</button>
								<button className="ui-button ui-button-quiet" type="button">
									View details
								</button>
								<button className="ui-button ui-button-danger" type="button">
									Delete
								</button>
								<button
									className="ui-button ui-button-secondary"
									disabled
									type="button"
								>
									Disabled
								</button>
							</div>
						</div>
						<div>
							<span className="ui-example-label">Icon and disclosure actions</span>
							<div className="ui-action-row">
								<button
									aria-label="More options"
									className="ui-icon-button"
									type="button"
								>
									<MoreIcon />
								</button>
								<button
									className="ui-button ui-button-secondary ui-button-disclosure"
									type="button"
								>
									Choose customer <ChevronIcon />
								</button>
								<button
									className="ui-button ui-button-primary"
									disabled
									type="button"
								>
									<span className="ui-spinner" /> Saving…
								</button>
							</div>
						</div>
					</div>
					<div className="ui-tabs" role="tablist" aria-label="Example tabs">
						{(['overview', 'activity', 'settings'] as const).map((tab) => (
							<button
								aria-selected={sampleTab === tab}
								className={sampleTab === tab ? 'active' : ''}
								key={tab}
								onClick={() => setSampleTab(tab)}
								role="tab"
								type="button"
							>
								{tab.slice(0, 1).toUpperCase() + tab.slice(1)}
							</button>
						))}
					</div>
					<div className="ui-tab-panel" role="tabpanel">
						<strong>{sampleTab.slice(0, 1).toUpperCase() + sampleTab.slice(1)}</strong>
						<span>
							The selected tab is clear without adding unnecessary decoration.
						</span>
					</div>
				</article>
			</section>

			<section className="ui-showcase-section" id="ui-forms">
				<div className="ui-section-heading">
					<div>
						<p className="ui-eyebrow">03 · Forms</p>
						<h3>Fields and controls</h3>
						<p>
							Text, date and time inputs, selects and action buttons share the same
							42px height.
						</p>
					</div>
				</div>
				<article className="ui-panel">
					<div className="ui-form-grid">
						<label className="ui-field">
							<span>Name</span>
							<input defaultValue="John Smith" />
							<small>Shown to visitors in chat.</small>
						</label>
						<label className="ui-field">
							<span>Email address</span>
							<input placeholder="name@example.com" type="email" />
						</label>
						<label className="ui-field">
							<span>Role</span>
							<select defaultValue="agent">
								<option value="agent">Agent</option>
								<option value="administrator">Administrator</option>
							</select>
						</label>
						<label className="ui-field ui-field-error">
							<span>Website URL</span>
							<input aria-invalid="true" defaultValue="not-a-url" />
							<small>Enter a valid URL beginning with https://</small>
						</label>
						<label className="ui-field ui-field-wide">
							<span>
								Notes <em>Optional</em>
							</span>
							<textarea
								defaultValue="A longer field uses the same label, border and focus treatment."
								rows={4}
							/>
						</label>
					</div>
					<div className="ui-control-grid">
						<div>
							<span className="ui-example-label">Selection controls</span>
							<label className="ui-choice">
								<input defaultChecked type="checkbox" />
								<span>Send me weekly reports</span>
							</label>
							<label className="ui-choice">
								<input name="availability" type="radio" defaultChecked />
								<span>Available</span>
							</label>
							<label className="ui-choice">
								<input name="availability" type="radio" />
								<span>Offline</span>
							</label>
						</div>
						<div className="ui-control-guidance">
							<span className="ui-example-label">Control rule</span>
							<strong>Use switches for immediate on/off settings</strong>
							<p>
								Checkboxes remain for selecting several values before a form is
								saved.
							</p>
						</div>
					</div>
				</article>
				<p className="ui-supporting">
					Operational-hours ranges use two independently draggable handles that cannot
					cross. Only the interval between them is highlighted. The seven-day range list
					is at most 1100px wide and its day labels are 14px at weight 500. Searchable
					team cards use equal gaps and vertically centre avatars, names and optional
					email lines.
				</p>

				<article className="ui-panel ui-date-range-example">
					<div className="ui-panel-header">
						<div>
							<h4>Shared date range</h4>
							<p>
								Every date-range filter uses this component and offers Today, This
								Week and This Month. Today is the normal default; Dashboard opens on
								the last seven days.
							</p>
						</div>
					</div>
					<DateRangePicker onChange={setSampleDateRange} value={sampleDateRange} />
					<p className="ui-supporting">
						Calendars appear before the start/end inputs. A selected range joins through
						square inner corners and rounds only its outside edges.
					</p>
				</article>

				<article className="ui-panel ui-settings-example">
					<div className="ui-panel-header">
						<div>
							<h4>Notifications</h4>
							<p>Choose which events notify you and how alerts are delivered.</p>
						</div>
						<span className="ui-badge ui-badge-success">Shared header</span>
					</div>
					<div className="ui-settings-grid">
						<div className="ui-setting-row">
							<div className="ui-setting-copy">
								<span className="ui-setting-title">New customers waiting</span>
								<span>Alert you when a new chat arrives.</span>
							</div>
							<button
								aria-checked={toggleEnabled}
								aria-label="New customers waiting"
								className="ui-switch"
								onClick={() => setToggleEnabled((current) => !current)}
								role="switch"
								type="button"
							>
								<span />
							</button>
						</div>
						<div className="ui-setting-row">
							<div className="ui-setting-copy">
								<span className="ui-setting-title">New messages received</span>
								<span>Alert when a visitor replies outside the open chat.</span>
							</div>
							<button
								aria-checked={messageNotificationsEnabled}
								aria-label="New messages received"
								className="ui-switch"
								onClick={() =>
									setMessageNotificationsEnabled((current) => !current)
								}
								role="switch"
								type="button"
							>
								<span />
							</button>
						</div>
						<label className="ui-setting-row">
							<span className="ui-setting-copy">
								<span className="ui-setting-title">Sound alert</span>
								<span>Choose a sound for enabled events.</span>
							</span>
							<select
								aria-label="Sound alert"
								className="ui-compact-select"
								defaultValue="bell"
							>
								<option value="none">None</option>
								<option value="chime">Chime</option>
								<option value="bell">Bell</option>
								<option value="pop">Pop</option>
							</select>
						</label>
						<div className="ui-setting-row ui-setting-row-wide-control">
							<div className="ui-setting-copy">
								<span className="ui-setting-title">Alert method</span>
								<span>Choose either a browser or system alert.</span>
							</div>
							<div className="ui-setting-actions">
								<div
									aria-label="Alert method"
									className="ui-segmented-control"
									role="group"
								>
									<button
										aria-pressed={sampleAlertMethod === 'browser'}
										onClick={() => setSampleAlertMethod('browser')}
										type="button"
									>
										Browser
									</button>
									<button
										aria-pressed={sampleAlertMethod === 'system'}
										onClick={() => setSampleAlertMethod('system')}
										type="button"
									>
										System
									</button>
								</div>
								<button
									className="ui-button ui-button-secondary ui-button-small"
									type="button"
								>
									Test
								</button>
							</div>
						</div>
					</div>
				</article>
			</section>

			<section className="ui-showcase-section" id="ui-panels">
				<div className="ui-section-heading">
					<div>
						<p className="ui-eyebrow">04 · Panels</p>
						<h3>Cards, metrics and grouping</h3>
						<p>
							Panels organise information; shadows are reserved for floating elements.
						</p>
					</div>
				</div>
				<div className="ui-metric-grid">
					<article className="ui-metric-card">
						<span>Customers</span>
						<strong>128</strong>
						<small>
							<b className="ui-positive">+8</b> this month
						</small>
					</article>
					<article className="ui-metric-card">
						<span>Team members</span>
						<strong>347</strong>
						<small>Across all customers</small>
					</article>
					<article className="ui-metric-card">
						<span>Active chats</span>
						<strong>42</strong>
						<small>
							<b className="ui-positive">Live</b> right now
						</small>
					</article>
					<article className="ui-metric-card">
						<span>AI spend</span>
						<strong>£84.21</strong>
						<small>Current month</small>
					</article>
				</div>
				<div className="ui-gallery-grid ui-gallery-grid-3">
					<article className="ui-panel">
						<div className="ui-panel-heading">
							<div>
								<p className="ui-eyebrow">Default</p>
								<h4>Standard panel</h4>
							</div>
							<button
								className="ui-button ui-button-quiet ui-button-small"
								type="button"
							>
								Manage
							</button>
						</div>
						<p className="ui-supporting">
							For most settings, summaries and content groups.
						</p>
					</article>
					<article className="ui-panel ui-panel-subtle">
						<div className="ui-panel-heading">
							<div>
								<p className="ui-eyebrow">Subtle</p>
								<h4>Secondary group</h4>
							</div>
						</div>
						<p className="ui-supporting">For nested or lower-priority information.</p>
					</article>
					<article className="ui-panel ui-panel-interactive">
						<div className="ui-panel-heading">
							<div>
								<p className="ui-eyebrow">Interactive</p>
								<h4>Selectable item</h4>
							</div>
							<ChevronIcon />
						</div>
						<p className="ui-supporting">
							The border and arrow communicate that the whole panel opens.
						</p>
					</article>
				</div>
				<article className="ui-panel ui-graphic-panel">
					<div className="ui-panel-header ui-panel-header-divided">
						<div>
							<h4>Visitor activity</h4>
							<p>Unique visitors · 30 days</p>
						</div>
						<span className="ui-badge ui-badge-success">Shared graphic header</span>
					</div>
					<div className="ui-chart-summary">
						<div>
							<strong>1,284</strong>
							<span>Total visitors</span>
						</div>
						<span className="ui-positive">+12.4%</span>
					</div>
					<div
						aria-label="Visitor activity over seven periods"
						className="ui-chart-sample"
						role="img"
					>
						<div className="ui-chart-bars">
							<span style={{ height: '38%' }} />
							<span style={{ height: '52%' }} />
							<span style={{ height: '46%' }} />
							<span style={{ height: '66%' }} />
							<span style={{ height: '58%' }} />
							<span style={{ height: '74%' }} />
							<span style={{ height: '86%' }} />
						</div>
						<div className="ui-chart-axis">
							<span>Week 1</span>
							<span>Week 2</span>
							<span>Week 3</span>
							<span>Week 4</span>
						</div>
					</div>
					<p className="ui-supporting">
						Line charts animate once on entry, expose each point to keyboard focus and
						show the point, time and value on hover or focus. Reduced-motion preferences
						remove the entry animation.
					</p>
				</article>
			</section>

			<section className="ui-showcase-section" id="ui-data">
				<div className="ui-section-heading">
					<div>
						<p className="ui-eyebrow">05 · Data display</p>
						<h3>Tables and lists</h3>
						<p>
							Headers are 44px and vertically centred with compact label line heights.
							Rows use 14px text, 7px vertical padding and one divider between
							adjoining surfaces. Report headings use “Chats” and “Avg”.
						</p>
					</div>
				</div>
				<article className="ui-panel ui-data-panel">
					<div className="ui-toolbar">
						<div>
							<h4>Team members</h4>
							<span>3 people</span>
						</div>
						<div className="ui-toolbar-actions">
							<label className="ui-search">
								<SearchIcon />
								<input aria-label="Search team members" placeholder="Search" />
							</label>
							<button className="ui-button ui-button-primary" type="button">
								Add team member
							</button>
						</div>
					</div>
					<div className="ui-table-scroll">
						<table className="ui-table">
							<thead>
								<tr>
									<th>Name</th>
									<th>Role</th>
									<th>Status</th>
									<th>Last active</th>
									<th>
										<span className="ui-visually-hidden">Actions</span>
									</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>
										<div className="ui-identity">
											<span className="ui-avatar">JS</span>
											<div>
												<strong>John Smith</strong>
												<small>john@example.com</small>
											</div>
										</div>
									</td>
									<td>Administrator</td>
									<td>
										<span className="ui-badge ui-badge-success">
											<i /> Available
										</span>
									</td>
									<td>Just now</td>
									<td>
										<button
											aria-label="Options for John Smith"
											className="ui-icon-button ui-icon-button-small"
											type="button"
										>
											<MoreIcon />
										</button>
									</td>
								</tr>
								<tr>
									<td>
										<div className="ui-identity">
											<span className="ui-avatar ui-avatar-ai">AI</span>
											<div>
												<strong>Alex Assistant</strong>
												<small>AI Agent</small>
											</div>
										</div>
									</td>
									<td>AI Agent</td>
									<td>
										<span className="ui-badge ui-badge-info">
											<i /> Processing
										</span>
									</td>
									<td>2 minutes ago</td>
									<td>
										<button
											aria-label="Options for Alex Assistant"
											className="ui-icon-button ui-icon-button-small"
											type="button"
										>
											<MoreIcon />
										</button>
									</td>
								</tr>
								<tr>
									<td>
										<div className="ui-identity">
											<span className="ui-avatar">RP</span>
											<div>
												<strong>Rita Patel</strong>
												<small>rita@example.com</small>
											</div>
										</div>
									</td>
									<td>Agent</td>
									<td>
										<span className="ui-badge ui-badge-neutral">
											<i /> Offline
										</span>
									</td>
									<td>Yesterday</td>
									<td>
										<button
											aria-label="Options for Rita Patel"
											className="ui-icon-button ui-icon-button-small"
											type="button"
										>
											<MoreIcon />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div className="ui-pagination">
						<span>Showing 1–3 of 28</span>
						<div>
							<button
								className="ui-button ui-button-secondary ui-button-small"
								disabled
								type="button"
							>
								Previous
							</button>
							<button className="ui-page-number active" type="button">
								1
							</button>
							<button className="ui-page-number" type="button">
								2
							</button>
							<button
								className="ui-button ui-button-secondary ui-button-small"
								type="button"
							>
								Next
							</button>
						</div>
					</div>
				</article>

				<div className="ui-gallery-grid ui-gallery-grid-2">
					<article className="ui-panel">
						<div className="ui-panel-heading">
							<div>
								<p className="ui-eyebrow">List</p>
								<h4>Recent activity</h4>
							</div>
						</div>
						<ul className="ui-list">
							<li>
								<span className="ui-list-mark ui-list-mark-blue" />
								<div>
									<strong>New customer created</strong>
									<span>Acme Couriers · 4 minutes ago</span>
								</div>
								<span className="ui-badge ui-badge-success">Complete</span>
							</li>
							<li>
								<span className="ui-list-mark ui-list-mark-amber" />
								<div>
									<strong>Knowledge import</strong>
									<span>12 of 46 pages · Processing</span>
								</div>
								<span className="ui-spinner ui-spinner-blue" />
							</li>
							<li>
								<span className="ui-list-mark ui-list-mark-red" />
								<div>
									<strong>Webhook delivery failed</strong>
									<span>Support inbox · 1 hour ago</span>
								</div>
								<button
									className="ui-button ui-button-quiet ui-button-small"
									type="button"
								>
									Retry
								</button>
							</li>
						</ul>
					</article>
					<article className="ui-panel">
						<div className="ui-panel-heading">
							<div>
								<p className="ui-eyebrow">Description list</p>
								<h4>Customer details</h4>
							</div>
						</div>
						<dl className="ui-description-list">
							<div>
								<dt>Company</dt>
								<dd>Acme Couriers Ltd</dd>
							</div>
							<div>
								<dt>Plan</dt>
								<dd>Business</dd>
							</div>
							<div>
								<dt>Created</dt>
								<dd>18 August 2026</dd>
							</div>
							<div>
								<dt>Account state</dt>
								<dd>
									<span className="ui-badge ui-badge-success">Active</span>
								</dd>
							</div>
						</dl>
					</article>
				</div>
			</section>

			<section className="ui-showcase-section" id="ui-feedback">
				<div className="ui-section-heading">
					<div>
						<p className="ui-eyebrow">06 · Feedback</p>
						<h3>Status, alerts and states</h3>
						<p>Colour is always supported by text or an icon.</p>
					</div>
				</div>
				<div className="ui-badge-row">
					<span className="ui-badge ui-badge-success">
						<i /> Active
					</span>
					<span className="ui-badge ui-badge-info">
						<i /> Processing
					</span>
					<span className="ui-badge ui-badge-warning">
						<i /> Needs attention
					</span>
					<span className="ui-badge ui-badge-danger">
						<i /> Failed
					</span>
					<span className="ui-badge ui-badge-neutral">
						<i /> Offline
					</span>
				</div>
				<div className="ui-callout-stack">
					<div className="ui-callout ui-callout-info">
						<div className="ui-callout-mark">i</div>
						<div>
							<strong>Information</strong>
							<p>The crawl will follow up to 200 eligible pages.</p>
						</div>
					</div>
					<div className="ui-callout ui-callout-success">
						<div className="ui-callout-mark">✓</div>
						<div>
							<strong>Changes saved</strong>
							<p>Your settings are now live.</p>
						</div>
					</div>
					<div className="ui-callout ui-callout-warning">
						<div className="ui-callout-mark">!</div>
						<div>
							<strong>Action recommended</strong>
							<p>This customer is approaching its monthly limit.</p>
						</div>
					</div>
					<div className="ui-callout ui-callout-danger">
						<div className="ui-callout-mark">×</div>
						<div>
							<strong>Could not complete the request</strong>
							<p>Check the URL and try again.</p>
						</div>
					</div>
				</div>
				<div className="ui-gallery-grid ui-gallery-grid-3">
					<article className="ui-state-card">
						<div className="ui-state-icon">+</div>
						<h4>No departments yet</h4>
						<p>Create a department to begin routing conversations.</p>
						<button className="ui-button ui-button-primary" type="button">
							Create department
						</button>
					</article>
					<article className="ui-state-card">
						<div className="ui-state-icon ui-state-icon-danger">!</div>
						<h4>Unable to load data</h4>
						<p>There was a problem connecting to the server.</p>
						<button className="ui-button ui-button-secondary" type="button">
							Try again
						</button>
					</article>
					<article className="ui-state-card ui-loading-card" aria-label="Loading example">
						<div className="ui-skeleton ui-skeleton-title" />
						<div className="ui-skeleton" />
						<div className="ui-skeleton ui-skeleton-short" />
						<div className="ui-skeleton ui-skeleton-button" />
					</article>
				</div>
			</section>

			<section className="ui-showcase-section" id="ui-overlays">
				<div className="ui-section-heading">
					<div>
						<p className="ui-eyebrow">07 · Overlays</p>
						<h3>Menus, dialogs and toasts</h3>
						<p>Floating elements share one border, radius and restrained shadow.</p>
					</div>
				</div>
				<article className="ui-panel">
					<div className="ui-overlay-actions">
						<div className="ui-menu-anchor">
							<button
								aria-expanded={menuOpen}
								className="ui-button ui-button-secondary ui-button-disclosure"
								onClick={() => setMenuOpen((current) => !current)}
								type="button"
							>
								Open menu <ChevronIcon />
							</button>
							{menuOpen && (
								<div className="ui-menu" role="menu">
									<button role="menuitem" type="button">
										View customer
									</button>
									<button role="menuitem" type="button">
										Duplicate
									</button>
									<hr />
									<button className="danger" role="menuitem" type="button">
										Delete
									</button>
								</div>
							)}
						</div>
						<button
							className="ui-button ui-button-primary"
							onClick={() => setDialogOpen(true)}
							type="button"
						>
							Open dialog
						</button>
						<button
							className="ui-button ui-button-secondary"
							onClick={() => setToastVisible(true)}
							type="button"
						>
							Show notification
						</button>
					</div>
				</article>
			</section>

			<section className="ui-showcase-section">
				<article className="ui-panel ui-rollout-panel">
					<div>
						<p className="ui-eyebrow">Acceptance boundary</p>
						<h3>What happens after testing</h3>
						<p>
							If accepted, this branch can be merged so these shared rules become the
							administration interface standard. If rejected, it can be discarded.
						</p>
					</div>
					<ul>
						<li>One page header and content width</li>
						<li>One panel, table and form pattern</li>
						<li>Token-based sizes and spacing only</li>
						<li>Existing light and dark themes preserved</li>
					</ul>
					<p className="ui-boundary-note">
						<strong>Separate:</strong> public chat bubbles and widget branding remain
						customer-configurable and are not governed by this administration UI
						standard.
					</p>
				</article>
			</section>

			{dialogOpen && (
				<div
					className="ui-dialog-backdrop"
					role="presentation"
					onMouseDown={(event) => {
						if (event.currentTarget === event.target) setDialogOpen(false);
					}}
				>
					<div
						aria-labelledby="ui-dialog-title"
						aria-modal="true"
						className="ui-dialog"
						role="dialog"
					>
						<div className="ui-dialog-heading">
							<div>
								<p className="ui-eyebrow">Confirmation</p>
								<h3 id="ui-dialog-title">Delete knowledge source?</h3>
							</div>
							<button
								aria-label="Close dialog"
								className="ui-icon-button"
								onClick={() => setDialogOpen(false)}
								type="button"
							>
								<CloseIcon />
							</button>
						</div>
						<p>
							This removes the source and all of its searchable chunks. This action
							cannot be undone.
						</p>
						<div className="ui-dialog-actions">
							<button
								className="ui-button ui-button-secondary"
								onClick={() => setDialogOpen(false)}
								type="button"
							>
								Cancel
							</button>
							<button
								className="ui-button ui-button-danger"
								onClick={() => setDialogOpen(false)}
								type="button"
							>
								Delete source
							</button>
						</div>
					</div>
				</div>
			)}

			{toastVisible && (
				<div aria-live="polite" className="ui-toast" role="status">
					<span className="ui-toast-mark">✓</span>
					<div>
						<strong>Settings saved</strong>
						<span>Your changes are now live.</span>
					</div>
					<button
						aria-label="Dismiss notification"
						className="ui-icon-button ui-icon-button-small"
						onClick={() => setToastVisible(false)}
						type="button"
					>
						<CloseIcon />
					</button>
				</div>
			)}
		</section>
	);
}
